import React, { MouseEventHandler, useCallback, useContext } from 'react'

import { AddToItineraryDatesRequiredModal } from './AddToItineraryDatesRequiredModal'
import { itemIsInItinerary, useItemOutsideOfTripDates } from './utils'
import EditTripDates from '../TripModal/Trips/EditTripDatesTP'

import AssistChip from 'components/Luxkit/Chips/AssistChip'
import LineCalendarAlertIcon from 'components/Luxkit/Icons/line/LineCalendarAlertIcon'
import LineCalendarCheckIcon from 'components/Luxkit/Icons/line/LineCalendarCheckIcon'
import LineCalendarPlusIcon from 'components/Luxkit/Icons/line/LineCalendarPlusIcon'
import Tooltip from 'components/Luxkit/Tooltip'
import ModalContext from 'contexts/ModalContext'
import { useAppDispatch } from 'hooks/reduxHooks'
import noop from 'lib/function/noop'
import { useTrip } from 'tripPlanner/hooks/api/trip'
import {
  usePatchTripItem,
} from 'tripPlanner/hooks/api/tripItem'
import useImmersiveModeHandlers from 'tripPlanner/hooks/useImmersiveModeHandlers'
import useOpenModal from 'tripPlanner/hooks/View/useOpenModal'
import {
  setCurrentSelectionId,
} from 'tripPlanner/reducers/actions'
import { TripItem } from 'tripPlanner/types/tripItem'
import { isCustomItem, isLeBooking } from 'tripPlanner/utils'
import { tripItemSelectionId } from 'tripPlanner/utils/itemSelection'

interface Props {
  item: TripItem
  onClickAddTripItemDates?: () => void
}

export function AddRemoveItineraryButton({
  item,
  onClickAddTripItemDates = noop,
}: Props) {
  const showModal = useContext(ModalContext)
  const { setImmersiveMode } = useImmersiveModeHandlers()
  const { data: trip } = useTrip({ tripId: item.tripId })
  // For now also using old way of modals until we refactor these out
  const openModal = useOpenModal()
  const setDates = useCallback(() => {
    openModal(EditTripDates, { tripId: item.tripId })
  }, [openModal, item.tripId])

  const showAddToItineraryDatesRequiredModal = useCallback(
    async(
      variation: 'trip' | 'leTripItem' | 'customTripItem',
    ): Promise<boolean> => {
      if (variation === 'leTripItem') {
        onClickAddTripItemDates()
        setImmersiveMode({
          tripId: item.tripId,
          sectionId: tripItemSelectionId(item.id),
        })
        return true
      }

      const didRequestAddTripDates = await showModal<boolean>(
        <AddToItineraryDatesRequiredModal variation={variation} />,
      )

      if (didRequestAddTripDates) {
        if (variation === 'trip') {
          setDates()
        } else {
          onClickAddTripItemDates()
          setImmersiveMode({
            tripId: item.tripId,
            sectionId: tripItemSelectionId(item.id),
          })
        }
      }

      return didRequestAddTripDates
    },
    [
      showModal,
      setDates,
      onClickAddTripItemDates,
      setImmersiveMode,
      item.tripId,
      item.id,
    ],
  )

  const isInItinerary = itemIsInItinerary(item, trip)

  const { mutate: patchItem } = usePatchTripItem()

  const removeFromItinerary = useCallback(() => {
    patchItem({
      tripId: item.tripId,
      tripItemId: item.id,
      tripItem: {
        type: item.type,
        startDate: null,
        endDate: null,
      },
    })
  }, [item.id, item.tripId, item.type, patchItem])

  const dispatch = useAppDispatch()

  const onClickAddToItinerary = useCallback<MouseEventHandler>(
    async(e) => {
      e.preventDefault()
      if (trip && (!trip.startDate || !trip.endDate)) {
        showAddToItineraryDatesRequiredModal('trip')
      } else if (isInItinerary) {
        removeFromItinerary()
      } else {
        const isIntendingToAddDates =
          await showAddToItineraryDatesRequiredModal(
            isCustomItem(item) ? 'customTripItem' : 'leTripItem',
          )
        if (isIntendingToAddDates) {
          dispatch(setCurrentSelectionId(tripItemSelectionId(item.id)))
        }
      }
    },
    [
      removeFromItinerary,
      dispatch,
      item,
      showAddToItineraryDatesRequiredModal,
      trip,
      isInItinerary,
    ],
  )

  // Check if this item is in itinerary but outside of the trip dates to show a warning icon.
  const isItemOutsideOfTripDates = useItemOutsideOfTripDates(item)

  let startIcon: JSX.Element
  if (isItemOutsideOfTripDates) {
    startIcon = <LineCalendarAlertIcon colour="warning" />
  } else if (isInItinerary) {
    startIcon = <LineCalendarCheckIcon colour="highlight-secondary-normal" />
  } else {
    startIcon = <LineCalendarPlusIcon />
  }

  if (item.sourceType === 'LE_Tour' || item.sourceType === 'LE_Cruise') {
    // We don't have the ability yet to add dates to saved tours and cruises (only booked will have dates)
    // So we will hide the add to itinerary button for tours and cruises without dates
    return null
  }

  if (isLeBooking(item)) {
    // Can't change dates on booked LE items
    return null
  }

  return (
    <Tooltip
      description={
        isInItinerary ? 'Remove from itinerary' : 'Add to itinerary'
      }
    >
      <AssistChip
        aria-label={
          isInItinerary ? 'Remove from itinerary' : 'Add to itinerary'
        }
        variant="filled"
        size="small"
        onClick={onClickAddToItinerary}
        startIcon={startIcon}
      />
    </Tooltip>
  )
}
